import { useState } from "react";
import { Phone, Crosshair, Copy, Siren, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { http, getDeviceId, errMsg } from "@/lib/api";
import { useI18n } from "@/lib/i18n";

const NUMBERS = [
  ["155", "Polis"], ["156", "Jandarma"], ["110", "İtfaiye"], ["122", "AFAD"], ["177", "Orman Yangını"], ["158", "Sahil Güvenlik"],
];
const TIPS = [
  "Sel ve su baskınında araçla suya girmeyin, yüksek yere çıkın.",
  "Yoğun siste kısa far ve sis lambası kullanın, takip mesafesini artırın.",
  "Dolu sırasında aracı güvenli bir yere çekin, camlardan uzak durun.",
  "Buzlanmada ani fren ve direksiyon hareketlerinden kaçının.",
];

export const SosModal = ({ open, onOpenChange, onCreated }) => {
  const { t } = useI18n();
  const [loc, setLoc] = useState(null);
  const [busy, setBusy] = useState(false);

  const locate = () => {
    if (!navigator.geolocation) return toast.error(t("Tarayıcınız konum desteklemiyor"));
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      (p) => { setLoc({ lat: p.coords.latitude, lon: p.coords.longitude }); setBusy(false); },
      () => { toast.error(t("Konum izni alınamadı")); setBusy(false); }
    );
  };
  const link = loc && `https://maps.google.com/?q=${loc.lat.toFixed(5)},${loc.lon.toFixed(5)}`;
  const copy = () => navigator.clipboard.writeText(link).then(() => toast.success(t("Konum bağlantısı kopyalandı")));
  const broadcast = async () => {
    try {
      const { data } = await http.post("/reports", { type: "sos", lat: loc.lat, lon: loc.lon, note: t("Acil yardım gerekiyor"), device_id: getDeviceId() });
      onCreated(data);
      toast.success(t("SOS topluluk haritasında paylaşıldı"));
    } catch (e) { toast.error(errMsg(e, t("SOS gönderilemedi"))); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="emergency-modal" className="z-[2000] max-h-[90vh] overflow-y-auto border-red-500/30 bg-[#11161F] text-slate-100 sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-2xl font-extrabold uppercase text-red-400"><Siren /> {t("Acil Çağrı")}</DialogTitle>
          <DialogDescription className="text-slate-400">{t("Hayati tehlike varsa hemen 112'yi arayın.")}</DialogDescription>
        </DialogHeader>
        <a data-testid="emergency-modal-call-btn" href="tel:112"
          className="keep-white sos-btn flex items-center justify-center gap-3 rounded-2xl bg-red-600 py-5 font-heading text-4xl font-black text-white hover:bg-red-500">
          <Phone size={30} /> {t("112'yi ARA")}
        </a>
        <div className="grid grid-cols-3 gap-2">
          {NUMBERS.map(([n, l]) => (
            <a key={n} data-testid={`emergency-number-${n}`} href={`tel:${n}`} className="rounded-xl border border-white/10 p-2 text-center hover:border-red-400/50 hover:bg-red-500/10">
              <div className="font-heading text-2xl font-bold text-white">{n}</div>
              <div className="text-[11px] text-slate-400">{t(l)}</div>
            </a>
          ))}
        </div>
        <div className="space-y-2 rounded-xl border border-white/10 bg-black/20 p-3">
          <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{t("Konumunu paylaş")}</div>
          {!loc ? (
            <button data-testid="emergency-locate-btn" onClick={locate} className="flex w-full items-center justify-center gap-2 rounded-full border border-sky-400/40 py-2 text-sm text-sky-300 hover:bg-sky-500/10">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Crosshair size={14} />} {t("Konumumu bul")}
            </button>
          ) : (
            <>
              <div data-testid="emergency-location" className="font-mono text-sm text-slate-200">{loc.lat.toFixed(5)}, {loc.lon.toFixed(5)}</div>
              <div className="flex gap-2">
                <button data-testid="emergency-copy-btn" onClick={copy} className="flex flex-1 items-center justify-center gap-1.5 rounded-full border border-white/15 py-2 text-xs hover:bg-white/5"><Copy size={13} /> {t("Bağlantıyı kopyala")}</button>
                <button data-testid="emergency-broadcast-btn" onClick={broadcast} className="flex flex-1 items-center justify-center gap-1.5 rounded-full bg-red-600/80 py-2 text-xs font-semibold hover:bg-red-500"><Siren size={13} /> {t("Haritada SOS bildir")}</button>
              </div>
            </>
          )}
        </div>
        <ul className="space-y-1.5 text-xs text-slate-400">
          {TIPS.map((tip) => <li key={tip} className="flex gap-2"><span className="text-red-400">•</span>{t(tip)}</li>)}
        </ul>
      </DialogContent>
    </Dialog>
  );
};
